import { MetricType } from '../constants/metric'
import type { MetricPeriod } from '../constants/metric'
import { ReportType } from '../constants/report'
import { ScheduledReportDateRangePresetName } from '../constants/scheduledReportConfig'
import type { ScheduledReportDateRangePreset } from '../constants/scheduledReportConfig'
import type { ScheduledReportConfig } from '../scheduledReportConfig'
import type { DataExportQuery } from './dataExportQuerySummary'
import {
  getDataExportQuerySummaryLines,
  getDataExportRemainingQuery,
  isEmptyDataExportQueryValue,
} from './dataExportQuerySummary'

type ReportFilterSummaryOptions = {
  venueName?: string
  venueNamesById?: Record<string, string>
  eventName?: string
  currentOrganizationId?: string
  currentOrganizationName?: string
}

type DateRangeLike = {
  $gte?: unknown
  $lte?: unknown
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }
  return value as Record<string, unknown>
}

function formatGenericLabel(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatUnknownValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ')
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

function removeNoFilters(lines: string[]): string[] {
  return lines.filter((line) => line !== 'No filters')
}

function getMetricTypeLabel(type: MetricType): string {
  if (type === MetricType.ADMISSION_GROUPS) return 'Admissions/Res'
  return formatGenericLabel(type)
}

function getMetricPeriodLabel(period: MetricPeriod): string {
  return formatGenericLabel(period)
}

export function reportOptionsToDataExportQuery(
  reportOptions: unknown
): DataExportQuery {
  const options = asRecord(reportOptions)
  const query =
    'query' in options ? asRecord(options.query) : (options as DataExportQuery)

  const normalized: DataExportQuery = { ...query }

  const exportDate = asRecord(query.exportDate) as DateRangeLike
  if (exportDate.$gte != null) {
    normalized.exportDateStart = exportDate.$gte
  }
  if (exportDate.$lte != null) {
    normalized.exportDateEnd = exportDate.$lte
  }
  delete normalized.exportDate

  return normalized
}

function getVenueLabel(
  value: unknown,
  venueNamesById?: Record<string, string>
): string | null {
  const values = Array.isArray(value) ? value : [value]
  const venueIds = values.filter(
    (item): item is string => typeof item === 'string' && item.length > 0
  )
  if (!venueIds.length) return null
  return venueIds
    .map((venueId) => venueNamesById?.[venueId] ?? venueId)
    .join(', ')
}

export function getReportFilterSummaryLines(
  reportType: ReportType,
  reportOptions: unknown,
  options: ReportFilterSummaryOptions = {}
): string[] {
  const query = reportOptionsToDataExportQuery(reportOptions)
  const lines = getDataExportQuerySummaryLines(query, {
    reportType,
    venueName: options.venueName,
    venueNamesById: options.venueNamesById,
    eventName: options.eventName,
  })

  if ('period' in query && !isEmptyDataExportQueryValue(query.period)) {
    lines.push(`Period: ${getMetricPeriodLabel(query.period as MetricPeriod)}`)
  }
  if ('type' in query && !isEmptyDataExportQueryValue(query.type)) {
    const rawTypes = Array.isArray(query.type) ? query.type : [query.type]
    const typed = rawTypes.filter(
      (value): value is MetricType => typeof value === 'string'
    )
    if (typed.length > 0) {
      lines.push(`Metrics: ${typed.map(getMetricTypeLabel).join(', ')}`)
    }
  }

  const remaining = getDataExportRemainingQuery(query)
  for (const [key, value] of Object.entries(remaining)) {
    if (key === 'period' || key === 'type') continue
    if (key === 'organizationId' && typeof value === 'string') {
      const isCurrentOrganization = value === options.currentOrganizationId
      const organizationLabel =
        isCurrentOrganization && options.currentOrganizationName
          ? options.currentOrganizationName
          : value
      lines.push(`Organization: ${organizationLabel}`)
      continue
    }
    if (key === 'venueId' || key === 'venue') {
      const venueLabel = getVenueLabel(value, options.venueNamesById)
      if (venueLabel) {
        lines.push(`Venues: ${venueLabel}`)
        continue
      }
    }
    lines.push(`${formatGenericLabel(key)}: ${formatUnknownValue(value)}`)
  }

  return lines
}

export function getScheduledReportFilterSummaryLines(
  config: ScheduledReportConfig,
  options: ReportFilterSummaryOptions = {}
): string[] {
  const lines: string[] = []
  const dateRangePreset = config.dateRangePreset as
    | ScheduledReportDateRangePreset
    | undefined
  if (dateRangePreset) {
    lines.push(
      `Date range preset: ${ScheduledReportDateRangePresetName[dateRangePreset]}`
    )
  }

  const reportLines = getReportFilterSummaryLines(
    config.reportType,
    config.reportOptions ?? {},
    options
  )
  lines.push(...removeNoFilters(reportLines))

  if (typeof config.includeReservations === 'boolean') {
    lines.push(
      `Include reservations: ${config.includeReservations ? 'Yes' : 'No'}`
    )
  }
  if (typeof config.includeAdmisions === 'boolean') {
    lines.push(`Include admissions: ${config.includeAdmisions ? 'Yes' : 'No'}`)
  }
  if (Array.isArray(config.events) && config.events.length > 0) {
    lines.push(`Events: ${config.events.length}`)
  }

  return lines.length > 0 ? lines : ['No filters']
}
